import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import zionDebug from '@fs/zion-debug'
import fetchLocations from './fetchLocations'
import { getAlpha2From3, getAlpha3From2, getUnlocalizedLocations } from './locationMappers'
import type { Iso3166Alpha2, Iso3166Alpha3, Iso3166Location, UseLocationsOptions } from './types'

const debug = zionDebug('zion:phone-number:useLocations')

export default function useLocations({
  // An optional array of strings of location codes (alpha2 or alpha3 values).
  // If falsy, then all supportedLocations will be used.
  // empty array can be passed
  // DO NOT PASS AN ARRAY LITERAL as there will be a bad performance penalty.
  onlyLocations,
  // mainly for testing/demo
  language: languageOverride,
  fallbackFn,
}: UseLocationsOptions = {}): Iso3166Location[] {
  const {
    i18n: { language: pageLanguage },
  } = useTranslation()
  const language = languageOverride || pageLanguage
  // calculate initial unlocalizedLocations to return from hook and also to use for fetching
  const unlocalizedLocations = useMemo(() => {
    if (!onlyLocations) {
      return getUnlocalizedLocations()
    }
    const isAlpha2 = onlyLocations[0]?.length === 2
    return onlyLocations.map((code) => {
      const alpha2 = isAlpha2 ? (code as Iso3166Alpha2) : getAlpha2From3(code as Iso3166Alpha3)
      const alpha3 = isAlpha2 ? getAlpha3From2(code as Iso3166Alpha2) : (code as Iso3166Alpha3)
      if (!alpha2 || !alpha3) {
        throw new Error(`Unsupported location code ${alpha2} ${alpha3}`)
      }
      return {
        alpha3,
        alpha2,
        // use alpha3 as initial text while fetching localized text
        text: alpha3,
      }
    })
  }, [onlyLocations])
  const [locations, setLocations] = useState<Iso3166Location[]>(unlocalizedLocations)

  useEffect(() => {
    // use zion debug here
    debug('Running useEffect')
    let cancelled = false
    async function fetchData(): Promise<void> {
      try {
        const newLocations = await fetchLocations({ unlocalizedLocations, language, fallbackFn })
        !cancelled && setLocations(newLocations)
      } catch (e) {
        console.error(e)
      }
    }
    fetchData()
    return () => {
      cancelled = true
    }
  }, [unlocalizedLocations, language, fallbackFn])

  return locations
}
